import type { PrismaClient } from '@prisma/client';
import { Router, type Request, type Response } from 'express';
import { audit } from '../../core/audit.js';
import type { Config } from '../../core/config.js';
import { HttpError } from '../../core/errors.js';
import { asyncHandler, clientIp } from '../../core/http.js';
import { authenticate, permissionsGranted } from '../auth/auth.middleware.js';
import {
  balanceTable,
  cashTable,
  incomeTable,
  statementTable,
  tableToCsv,
  tableToPdf,
  vatTable,
  whtTable,
  type ReportTable,
} from './reports.export.js';
import {
  balanceSheet,
  cashFlow,
  clientStatement,
  incomeStatement,
  parseRange,
  vatSummary,
  vendorStatement,
  whtReport,
} from './reports.service.js';

interface Deps {
  db: PrismaClient;
  config: Config;
}

/** Financial statements and tax reports. JSON for the screen; `format=csv|pdf` for downloads. */
export function createReportsRouter({ db, config }: Deps) {
  const router = Router();
  router.use('/reports', authenticate({ db, config }));

  const can = (req: Request, p: string) => permissionsGranted(req.auth!.permissions, p);
  const needView = (req: Request) => {
    if (!can(req, 'accounting.report.view'))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
  };
  const actor = (req: Request) => ({
    companyId: req.auth!.companyId,
    userId: req.auth!.userId,
    ip: clientIp(req),
  });

  /** Sends JSON, or (with format=csv|pdf) a download that is logged and audited like every export. */
  async function respond<T>(
    req: Request,
    res: Response,
    entity: string,
    data: T,
    table: (d: T) => ReportTable,
  ) {
    const format = typeof req.query.format === 'string' ? req.query.format : 'json';
    if (format === 'json') {
      res.json(data);
      return;
    }
    if (format !== 'csv' && format !== 'pdf')
      throw new HttpError(400, 'VALIDATION_ERROR', 'format must be json, csv or pdf');
    if (!can(req, 'accounting.report.export'))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to export reports');
    const t = table(data);
    const company = await db.company.findUniqueOrThrow({ where: { id: req.auth!.companyId } });
    await db.exportLog.create({
      data: {
        companyId: req.auth!.companyId,
        userId: req.auth!.userId,
        entity: `report.${entity}`,
        filters: req.query as never,
        rowCount: t.rows.length,
      },
    });
    await audit(db, actor(req), {
      action: 'export.report',
      entity: `report.${entity}`,
      after: { format, filters: req.query },
    });
    const name = `${entity}.${format}`;
    res.setHeader('Content-Disposition', `attachment; filename="${name}"`);
    res.setHeader('X-Content-Type-Options', 'nosniff');
    if (format === 'csv') {
      res.setHeader('Content-Type', 'text/csv; charset=utf-8');
      res.send(tableToCsv(t));
    } else {
      res.setHeader('Content-Type', 'application/pdf');
      res.send(await tableToPdf(t, company));
    }
  }

  router.get(
    '/reports/income-statement',
    asyncHandler(async (req, res) => {
      needView(req);
      const { from, to } = parseRange(req.query as Record<string, unknown>);
      const compare = ['previous', 'lastyear'].includes(String(req.query.compare))
        ? (String(req.query.compare) as 'previous' | 'lastyear')
        : 'none';
      await respond(
        req,
        res,
        'income-statement',
        await incomeStatement(db, req.auth!.companyId, { from, to, compare }),
        incomeTable,
      );
    }),
  );
  router.get(
    '/reports/balance-sheet',
    asyncHandler(async (req, res) => {
      needView(req);
      const asOf = parseRange({ to: req.query.asOf }).to;
      await respond(
        req,
        res,
        'balance-sheet',
        await balanceSheet(db, req.auth!.companyId, asOf),
        balanceTable,
      );
    }),
  );
  router.get(
    '/reports/cash-flow',
    asyncHandler(async (req, res) => {
      needView(req);
      const { from, to } = parseRange(req.query as Record<string, unknown>);
      await respond(
        req,
        res,
        'cash-flow',
        await cashFlow(db, req.auth!.companyId, { from, to }),
        cashTable,
      );
    }),
  );
  router.get(
    '/reports/vat',
    asyncHandler(async (req, res) => {
      needView(req);
      const { from, to } = parseRange(req.query as Record<string, unknown>);
      await respond(
        req,
        res,
        'vat-summary',
        await vatSummary(db, req.auth!.companyId, from, to),
        vatTable,
      );
    }),
  );
  router.get(
    '/reports/wht',
    asyncHandler(async (req, res) => {
      needView(req);
      const { from, to } = parseRange(req.query as Record<string, unknown>);
      await respond(
        req,
        res,
        'wht-report',
        await whtReport(db, req.auth!.companyId, from, to),
        whtTable,
      );
    }),
  );

  // Statements: allowed for the books, or for the people who deal with that client/vendor.
  const partyAllowed = async (req: Request, id: number, role: 'client' | 'vendor') => {
    const perm = role === 'client' ? 'sales.client.view' : 'purchases.vendor.view';
    if (can(req, 'accounting.report.view')) return;
    if (!can(req, perm))
      throw new HttpError(403, 'FORBIDDEN', 'You do not have permission to do this');
    const p = await db.party.findFirst({
      where: {
        id,
        companyId: req.auth!.companyId,
        ...(req.auth!.scope === 'own' ? { ownerId: req.auth!.userId } : {}),
      },
    });
    if (!p) throw new HttpError(404, 'NOT_FOUND', 'Not found');
  };
  router.get(
    '/reports/statements/client/:id',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      await partyAllowed(req, id, 'client');
      const { from, to } = parseRange(req.query as Record<string, unknown>, {
        from: `${new Date().getUTCFullYear()}-01-01`,
        to: new Date().toISOString().slice(0, 10),
      });
      const currency =
        typeof req.query.currency === 'string' ? req.query.currency.toUpperCase() : undefined;
      await respond(
        req,
        res,
        'client-statement',
        await clientStatement(db, req.auth!.companyId, id, { from, to, currency }),
        statementTable,
      );
    }),
  );
  router.get(
    '/reports/statements/vendor/:id',
    asyncHandler(async (req, res) => {
      const id = Number(req.params.id);
      await partyAllowed(req, id, 'vendor');
      const { from, to } = parseRange(req.query as Record<string, unknown>, {
        from: `${new Date().getUTCFullYear()}-01-01`,
        to: new Date().toISOString().slice(0, 10),
      });
      const currency =
        typeof req.query.currency === 'string' ? req.query.currency.toUpperCase() : undefined;
      await respond(
        req,
        res,
        'vendor-statement',
        await vendorStatement(db, req.auth!.companyId, id, { from, to, currency }),
        statementTable,
      );
    }),
  );

  return router;
}
